import { ProductCardBuilder } from '../ProductCard/ProductCardBuilder';
import { PromoTileBuilder } from '../PromoTile/PromoTileBuilder';
import QuickSearchDrawerToggleLogic from './QuickSearchDrawer';
import { trackAutocompleteSelect } from '../../utils/ConstructorTracking';
import { productClickTracking } from '../GTMEvents';
import { constructorParamsBuilder } from '../../core/ConstructorSdk';

/**
 * QuickSearch component for Constructor API autocomplete and recommendations
 * Manages three states: no-query, results, no-results
 */
class QuickSearch {
  // Private properties
  #debounceTimer = null;
  #debounceDelay = 300;
  #currentQuery = '';
  #resultId = '';
  #currentState = 'no-query'; // 'no-query' | 'results' | 'no-results'
  #wasMobile = false;
  #isNavigating = false;
  #pillScrollHandlers = new Map();

  // Cached recommendation data for fallback
  #cachedRecommendations = {
    popularSearches: [],
    trendingCollections: [],
    trendingProducts: [],
    resultIds: {
      popular: null,
      collections: null,
      products: null,
    },
    fetched: false,
  };

  // Pod IDs from theme settings
  #podIds = {
    popularSearches: window.cnstrc?.quickSearch?.popularSearchesPodId,
    trendingCollections: window.cnstrc?.quickSearch?.trendingCollectionsPodId,
    trendingProducts: window.cnstrc?.quickSearch?.trendingProductsPodId,
  };

  // DOM Selectors
  #selectors = {
    wrapper: '[data-search-drawer]',
    // Desktop: header input; Mobile: drawer input
    inputDesktop: '[data-quick-search-desktop-drawer-input] [data-cnstrc-search-input]',
    inputMobile: '[data-quick-search-drawer-input]',
    form: '[data-cnstrc-search-form]',

    // Buttons
    goButton: '[data-quick-search-go]',
    clearButton: '[data-quick-search-clear]',

    // State containers
    noQueryState: '[data-quick-search-no-query]',
    resultsState: '[data-quick-search-results]',
    noResultsState: '[data-quick-search-no-results]',

    // No Query containers
    popularSearches: '[data-quick-search-popular]',
    popularSearchesDesktop: '[data-quick-search-popular-desktop]',
    trendingCollections: '[data-quick-search-trending-collections]',
    trendingProducts: '[data-quick-search-trending-products]',

    // Results containers
    suggestions: '[data-quick-search-suggestions]',
    collections: '[data-quick-search-collections]',
    suggestionsTitle: '[data-quick-search-suggestions-title]',
    collectionsTitle: '[data-quick-search-collections-title]',
    productsTitle: '[data-quick-search-products-title]',
    products: '[data-quick-search-products]',
    productCount: '[data-quick-search-product-count]',
    viewAll: '[data-quick-search-view-all]',

    // No Results containers
    failedQuery: '[data-quick-search-failed-query]',
    fallbackPopular: '[data-quick-search-fallback-popular]',
    fallbackPopularDesktop: '[data-quick-search-fallback-popular-desktop]',
    fallbackCollections: '[data-quick-search-fallback-collections]',
    fallbackProducts: '[data-quick-search-fallback-products]',
  };

  constructor() {
    this.wrapper = null;
    this.input = null;
    this.form = null;

    // Bound event handlers (for cleanup)
    this.#handleInputBound = this.#handleInput.bind(this);
    this.#handleKeydownBound = this.#handleKeydown.bind(this);
    this.#handleFocusBound = this.#handleFocus.bind(this);
    this.#handleSubmitBound = this.#handleSubmit.bind(this);
    this.#handleClearBound = this.#handleClear.bind(this);
    this.#handleGoBound = this.#handleGo.bind(this);
    this.#handleDrawerCloseBound = this.#handleDrawerClose.bind(this);
    this.#handleDrawerOpenBound = this.#handleDrawerOpen.bind(this);
    this.#handleResizeBound = this.#handleResize.bind(this);
    this.#handleDrawerScrollBound = this.#handleDrawerScroll.bind(this);
    this.#handleAutocompleteClickBound = this.#handleAutocompleteClick.bind(this);
  }

  // Bound handlers stored as instance properties
  #handleInputBound;
  #handleKeydownBound;
  #handleFocusBound;
  #handleSubmitBound;
  #handleClearBound;
  #handleGoBound;
  #handleDrawerCloseBound;
  #handleDrawerOpenBound;
  #handleResizeBound;
  #handleDrawerScrollBound;
  #handleAutocompleteClickBound;

  /**
   * Check if we're on mobile viewport
   * @returns {Boolean}
   * @private
   */
  #isMobile() {
    return window.innerWidth < 990;
  }

  /**
   * Initialize the QuickSearch component
   */
  init() {
    // Query required DOM elements
    this.wrapper = document.querySelector(this.#selectors.wrapper);
    this.#selectInput();
    this.form = document.querySelector(this.#selectors.form);

    if (!this.wrapper || !this.input) {
      console.warn('QuickSearch: Required DOM elements not found');
      return;
    }

    // Bind all event handlers
    this.#bindEvents();
    QuickSearchDrawerToggleLogic();

    // Recommendations are loaded lazily on first drawer open (see #handleDrawerOpen)
  }

  /**
   * Select the appropriate input based on viewport
   * Desktop: header input; Mobile: drawer input
   * @private
   */
  #selectInput() {
    const selector = this.#isMobile() ? this.#selectors.inputMobile : this.#selectors.inputDesktop;
    this.input = document.querySelector(selector);
  }

  /**
   * Bind all event listeners
   * @private
   */
  #bindEvents() {
    // Input handler with debouncing
    this.input.addEventListener('input', this.#handleInputBound);

    // Keydown for Enter key submission
    this.input.addEventListener('keydown', this.#handleKeydownBound);

    // Focus handler
    this.input.addEventListener('focus', this.#handleFocusBound);

    // Form submission prevention
    if (this.form) {
      this.form.addEventListener('submit', this.#handleSubmitBound);
    }

    // Clear button
    const clearButton = this.wrapper.querySelector(this.#selectors.clearButton);
    if (clearButton) {
      clearButton.addEventListener('click', this.#handleClearBound);
    }

    // GO button (direct click handler for both desktop and mobile)
    const goButtons = document.querySelectorAll(this.#selectors.goButton);
    goButtons.forEach((btn) => {
      btn.addEventListener('click', this.#handleGoBound);
    });

    // Listen for search drawer close (close button, overlay click, escape key)
    document.addEventListener('searchDrawerClosed', this.#handleDrawerCloseBound);

    // Listen for search drawer open to fetch recommendations and focus input
    document.addEventListener('searchDrawerOpened', this.#handleDrawerOpenBound);

    // Listen for viewport changes to re-bind input handlers
    window.addEventListener('resize', this.#handleResizeBound);

    // Dismiss virtual keyboard on scroll (mobile)
    this.wrapper.addEventListener('scroll', this.#handleDrawerScrollBound, { passive: true });

    // Constructor autocomplete select + GTM productClick tracking (capture-phase so the
    // beacon fires before the anchor navigates away)
    this.wrapper.addEventListener('click', this.#handleAutocompleteClickBound, true);

    // Track initial mobile state
    this.#wasMobile = this.#isMobile();
  }

  // ============================================
  // STATE MANAGEMENT
  // ============================================

  /**
   * Set the current state and update the DOM attribute
   * @param {String} state - 'no-query' | 'results' | 'no-results'
   * @private
   */
  #setState(state) {
    this.#currentState = state;
    this.wrapper.setAttribute('data-quick-search-state', state);
  }

  /**
   * Update GO and Clear button visibility based on query presence
   * @param {Boolean} hasQuery - Whether input has text
   * @private
   */
  #updateButtonVisibility(hasQuery) {
    const goButton = this.wrapper.querySelector(this.#selectors.goButton);
    const clearButton = this.wrapper.querySelector(this.#selectors.clearButton);
    const desktopGoButton = document.querySelector(
      `[data-quick-search-desktop-drawer-input] ${this.#selectors.goButton}`,
    );

    goButton?.classList.toggle('quick-search-c-form__go-btn--visible', hasQuery);
    clearButton?.classList.toggle('quick-search-c-form__clear-btn--visible', hasQuery);
    desktopGoButton?.classList.toggle('quick-search-c-form__go-btn--visible', hasQuery);
  }

  // ============================================
  // EVENT HANDLERS
  // ============================================

  /**
   * Handle input changes with debouncing
   * @param {Event} event - Input event
   * @private
   */
  #handleInput(event) {
    const query = event.target.value.trim();

    // Update button visibility
    this.#updateButtonVisibility(!!query);

    // Clear previous debounce timer
    if (this.#debounceTimer) {
      clearTimeout(this.#debounceTimer);
    }

    // If query is empty, show No Query state
    if (!query) {
      this.#currentQuery = '';
      this.#setState('no-query');
      return;
    }

    // Debounce the API call
    this.#debounceTimer = setTimeout(() => {
      if (query !== this.#currentQuery) {
        this.#currentQuery = query;
        this.#fetchAutocomplete(query);
      }
    }, this.#debounceDelay);
  }

  /**
   * Handle keydown events (Enter key submission)
   * @param {KeyboardEvent} event - Keyboard event
   * @private
   */
  #handleKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault();
      const query = this.input.value.trim();
      if (query) {
        this.#navigateToSearch(query);
      }
    }
  }

  /**
   * Handle focus events on input
   * @private
   */
  #handleFocus() {
    // If input is empty, show No Query state
    if (!this.input.value.trim()) {
      this.#setState('no-query');
    }
  }

  /**
   * Handle search drawer opened event - focus input on desktop
   * @private
   */
  #handleDrawerOpen() {
    if (!this.#cachedRecommendations.fetched) {
      this.#fetchRecommendations();
    }

    // On desktop, focus the header input so user can type immediately
    if (!this.#isMobile() && this.input) {
      this.input.focus();
    }
  }

  /**
   * Handle form submission
   * @param {Event} event - Submit event
   * @private
   */
  #handleSubmit(event) {
    event.preventDefault();
    const query = this.input.value.trim();
    if (query) {
      this.#navigateToSearch(query);
    }
  }

  /**
   * Handle GO button click
   * @param {Event} event - Click event
   * @private
   */
  #handleGo(event) {
    event.preventDefault();
    const query = this.input.value.trim();
    if (query) {
      this.#navigateToSearch(query);
    }
  }

  /**
   * Handle clear button click
   * @private
   */
  #handleClear() {
    this.input.value = '';
    this.#currentQuery = '';
    this.#updateButtonVisibility(false);
    this.#setState('no-query');
    this.input.focus();
  }

  /**
   * Handle drawer close event
   * @private
   */
  #handleDrawerClose() {
    if (this.#isNavigating) return;

    // Clear debounce timer
    if (this.#debounceTimer) {
      clearTimeout(this.#debounceTimer);
      this.#debounceTimer = null;
    }

    // Reset input and state so reopening shows a clean no-query view
    if (this.input) {
      this.input.value = '';
    }
    this.#currentQuery = '';
    this.#updateButtonVisibility(false);
    this.#setState('no-query');

    // Clear rendered results so stale content doesn't flash on reopen
    const resultContainers = [this.#selectors.suggestions, this.#selectors.collections, this.#selectors.products];
    resultContainers.forEach((selector) => {
      const el = this.wrapper?.querySelector(selector);
      if (el) el.innerHTML = '';
    });
  }

  /**
   * Dismiss virtual keyboard when scrolling inside the drawer on mobile
   * @private
   */
  #handleDrawerScroll() {
    if (!this.#isMobile()) return;
    if (document.activeElement === this.input) {
      this.input.blur();
    }
  }

  /**
   * Handle autocomplete click tracking for Constructor and GTM
   * @param {Event} e - Click event
   * @private
   */
  #handleAutocompleteClick(e) {
    const item = e.target.closest('[data-cnstrc-item="autosuggest"]');
    if (!item) return;

    const section = item.getAttribute('data-cnstrc-item-section') || 'Products';
    trackAutocompleteSelect(this.#currentQuery, {
      originalQuery: this.#currentQuery,
      section,
      ...(item.getAttribute('data-cnstrc-item-id') && { itemId: item.getAttribute('data-cnstrc-item-id') }),
    });

    if (section === 'Products') {
      productClickTracking(
        {
          id: item.getAttribute('data-cnstrc-item-id'),
          name: item.getAttribute('data-cnstrc-item-name'),
          price: item.getAttribute('data-cnstrc-item-price'),
        },
        'Top Products - Search bar',
      );
    }
  }

  /**
   * Handle viewport resize - re-bind input handlers when crossing mobile/desktop threshold
   * @private
   */
  #handleResize() {
    const isMobileNow = this.#isMobile();

    // Only re-bind if we crossed the threshold
    if (isMobileNow === this.#wasMobile) return;

    // Save current value before switching
    const previousValue = this.input?.value || '';

    // Unbind from old input
    if (this.input) {
      this.input.removeEventListener('input', this.#handleInputBound);
      this.input.removeEventListener('keydown', this.#handleKeydownBound);
      this.input.removeEventListener('focus', this.#handleFocusBound);
    }

    // Select new input based on current viewport
    this.#selectInput();

    // Restore value to new input and sync UI state
    if (this.input) {
      this.input.value = previousValue;
      this.#updateButtonVisibility(!!previousValue.trim());

      this.input.addEventListener('input', this.#handleInputBound);
      this.input.addEventListener('keydown', this.#handleKeydownBound);
      this.input.addEventListener('focus', this.#handleFocusBound);
    }

    // Update tracked state
    this.#wasMobile = isMobileNow;

    // Re-evaluate pill fades after breakpoint change
    this.#pillScrollHandlers.forEach((_handler, container) => {
      this.#updatePillFadeClasses(container);
    });
  }

  /**
   * Update fade modifier classes on a pill container based on scroll position
   * @param {HTMLElement} container - The pills container element
   * @private
   */
  #updatePillFadeClasses(container) {
    const fadeClasses = [
      'quick-search-c-pills--fade-right',
      'quick-search-c-pills--fade-left',
      'quick-search-c-pills--fade-both',
    ];
    container.classList.remove(...fadeClasses);

    const { scrollWidth, clientWidth, scrollLeft } = container;

    // Content fits in view — no fade needed
    if (scrollWidth - clientWidth <= 2) return;

    if (scrollLeft <= 2) {
      container.classList.add('quick-search-c-pills--fade-right');
    } else if (scrollLeft + clientWidth >= scrollWidth - 2) {
      container.classList.add('quick-search-c-pills--fade-left');
    } else {
      container.classList.add('quick-search-c-pills--fade-both');
    }
  }

  /**
   * Initialize scroll-based fade effects on a pills container
   * @param {HTMLElement} container - The pills container element
   * @private
   */
  #initPillFades(container) {
    if (!container || this.#pillScrollHandlers.has(container)) return;

    const handler = () => this.#updatePillFadeClasses(container);
    container.addEventListener('scroll', handler, { passive: true });
    this.#pillScrollHandlers.set(container, handler);

    // Set initial fade state
    this.#updatePillFadeClasses(container);
  }

  /**
   * Remove all pill fade scroll listeners and classes
   * @private
   */
  #cleanupPillFades() {
    this.#pillScrollHandlers.forEach((handler, container) => {
      container.removeEventListener('scroll', handler);
      container.classList.remove(
        'quick-search-c-pills--fade-right',
        'quick-search-c-pills--fade-left',
        'quick-search-c-pills--fade-both',
      );
    });
    this.#pillScrollHandlers.clear();
  }

  /**
   * Navigate to search results page
   * @param {String} query - Search query
   * @private
   */
  #navigateToSearch(query) {
    if (!query) return;
    this.#isNavigating = true;
    window.location.href = `/pages/search?q=${encodeURIComponent(query)}`;
  }

  // ============================================
  // RECOMMENDATIONS API
  // ============================================

  /**
   * Load No Query recommendations from cached Shopify metafields (populated by middleware)
   * @private
   */
  async #fetchRecommendations() {
    if (this.#cachedRecommendations.fetched) return;
    this.#cachedRecommendations.fetched = true;

    if (!window.cnstrcSdk) {
      console.warn('[QuickSearch] Missing cnstrcSdk, aborting recommendations fetch');
      return;
    }

    const params = {};
    const productParams = {};
    constructorParamsBuilder(productParams, true);

    try {
      // Helper to wrap promise with timeout
      const withTimeout = (promise, name, timeoutMs = 10000) => {
        return Promise.race([
          promise,
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error(`${name} timed out after ${timeoutMs}ms`)), timeoutMs),
          ),
        ]);
      };

      const popularSearchesParams = { ...params, section: 'Search Suggestions' };
      const trendingCollectionsParams = { ...params, section: 'Categories', hiddenFields: [`handle`, `handles`] };
      const trendingProductsParams = productParams;

      const [popularResult, collectionsResult, productsResult] = await Promise.allSettled([
        this.#podIds.popularSearches
          ? withTimeout(
              window.cnstrcSdk.recommendations.getRecommendations(this.#podIds.popularSearches, popularSearchesParams),
              'Search Suggestions',
            )
          : Promise.resolve(null),
        this.#podIds.trendingCollections
          ? withTimeout(
              window.cnstrcSdk.recommendations.getRecommendations(
                this.#podIds.trendingCollections,
                trendingCollectionsParams,
              ),
              'Trending collections',
            )
          : Promise.resolve(null),
        this.#podIds.trendingProducts
          ? withTimeout(
              window.cnstrcSdk.recommendations.getRecommendations(
                this.#podIds.trendingProducts,
                trendingProductsParams,
              ),
              'Trending products',
            )
          : Promise.resolve(null),
      ]);

      // Extract values from settled results
      const popularRes = popularResult.status === 'fulfilled' ? popularResult.value : null;
      const collectionsRes = collectionsResult.status === 'fulfilled' ? collectionsResult.value : null;
      const productsRes = productsResult.status === 'fulfilled' ? productsResult.value : null;

      const popularSearches = popularRes?.response?.results || [];
      const trendingCollections = collectionsRes?.response?.results || [];
      const trendingProducts = productsRes?.response?.results || [];

      this.#cachedRecommendations = {
        popularSearches,
        trendingCollections,
        trendingProducts,
        resultIds: {
          popular: popularRes?.result_id,
          collections: collectionsRes?.result_id,
          products: productsRes?.result_id,
        },
        fetched: true,
      };

      this.#renderNoQueryState();
    } catch (error) {
      console.error('[QuickSearch] Recommendations fetch error:', error);
    }
  }

  // ============================================
  // AUTOCOMPLETE API
  // ============================================

  /**
   * Fetch autocomplete results from Constructor API
   * @param {String} query - Search query
   * @private
   */
  async #fetchAutocomplete(query) {
    if (!window.cnstrcSdk) {
      console.warn('[QuickSearch] Missing cnstrcSdk, aborting fetch');
      return;
    }

    try {
      const country = window.Shopify?.country || 'US';
      const isIntl = country !== 'US';
      const params = {
        resultsPerSection: {
          Products: 6,
          'Search Suggestions': 8,
          Categories: 8,
        },
        hiddenFields: [`price${country}`, `compareAtPrice${country}`, `handles`, `handle`],
        filtersPerSection: {
          Products: isIntl ? { variantVisibleBrowseIntl: 'True' } : { variantVisibleBrowse: 'True' },
        },
        preFilterExpression: {
          and: [{ not: { name: 'promo_size', value: '1' } }, { not: { name: 'promo_size', value: '2' } }],
        },
      };
      isIntl && params.hiddenFields.push('inventory_quantity_international');

      const response = await window.cnstrcSdk.autocomplete.getAutocompleteResults(query, params);

      this.#handleAutocompleteResponse(response, query);
    } catch (error) {
      console.error('[QuickSearch] Autocomplete fetch error:', error);
      this.#setState('no-results');
      this.#renderNoResultsState(query);
    }
  }

  /**
   * Handle autocomplete API response
   * @param {Object} response - Constructor API response
   * @param {String} query - Search query
   * @private
   */
  #handleAutocompleteResponse(response, query) {
    const { sections, result_id, total_num_results_per_section } = response;

    const products = sections?.Products || [];
    const suggestions = sections?.['Search Suggestions'] || [];
    const categories = sections?.Categories || [];
    const productCount = total_num_results_per_section?.Products || products.length;

    // Store result_id for tracking
    this.#resultId = result_id || '';

    // Determine state based on results — only show no-results if all three sections are empty
    const hasResults = products.length > 0 || suggestions.length > 0 || categories.length > 0;

    if (!hasResults) {
      this.#setState('no-results');
      this.#renderNoResultsState(query);
    } else {
      this.#setState('results');
      this.#renderResultsState(products, suggestions, categories, productCount, query);
    }
  }

  // ============================================
  // RENDER METHODS - NO QUERY STATE
  // ============================================

  /**
   * Render the No Query state with recommendations
   * @private
   */
  #renderNoQueryState() {
    const { popularSearches, trendingCollections, trendingProducts, resultIds } = this.#cachedRecommendations;

    // Render popular searches as pills (mobile)
    this.#renderPopularSearchPills(popularSearches, this.wrapper.querySelector(this.#selectors.popularSearches));

    // Render popular searches as text links (desktop)
    this.#renderPopularSearchTextLinks(
      popularSearches,
      this.wrapper.querySelector(this.#selectors.popularSearchesDesktop),
    );

    // Render trending collections as pills
    this.#renderCollectionPills(
      trendingCollections,
      this.wrapper.querySelector(this.#selectors.trendingCollections),
      'recommendation',
    );

    // Render trending products
    this.#renderProductCards(
      trendingProducts,
      resultIds.products,
      this.wrapper.querySelector(this.#selectors.trendingProducts),
      'recommendation',
    );
  }

  /**
   * Render popular search pills (mobile)
   * @param {Array} items - Popular search items
   * @param {HTMLElement} container - Target container
   * @private
   */
  #renderPopularSearchPills(items, container) {
    if (!container) return;
    container.innerHTML = '';

    items.forEach((item) => {
      const searchValue = item.value || item.data?.value || '';
      if (!searchValue) return;

      const pill = document.createElement('a');
      pill.classList.add('quick-search-c-pills__item');
      pill.href = `/pages/search?q=${encodeURIComponent(searchValue)}`;
      pill.textContent = searchValue;

      // Constructor tracking attributes
      pill.setAttribute('data-cnstrc-item', 'recommendation');
      pill.setAttribute('data-cnstrc-item-name', searchValue);

      container.appendChild(pill);
    });

    this.#initPillFades(container);
  }

  /**
   * Render popular search text links (desktop)
   * @param {Array} items - Popular search items
   * @param {HTMLElement} container - Target container
   * @private
   */
  #renderPopularSearchTextLinks(items, container) {
    if (!container) return;
    container.innerHTML = '';

    items.forEach((item) => {
      const searchValue = item.value || item.data?.value || '';
      if (!searchValue) return;

      const link = document.createElement('a');
      link.classList.add('quick-search-c-text-links__item');
      link.href = `/pages/search?q=${encodeURIComponent(searchValue)}`;
      link.textContent = searchValue;

      // Constructor tracking attributes
      link.setAttribute('data-cnstrc-item', 'recommendation');
      link.setAttribute('data-cnstrc-item-name', searchValue);

      container.appendChild(link);
    });
  }

  /**
   * Render collection pills (for trending or search results)
   * @param {Array} categories - Category items
   * @param {HTMLElement} container - Target container
   * @param {String} trackingType - 'recommendation' or 'autosuggest'
   * @private
   */
  #renderCollectionPills(categories, container, trackingType = 'autosuggest') {
    if (!container) return;
    container.innerHTML = '';

    categories.forEach((category) => {
      const name = category.value || '';
      if (!name) return;

      const pill = document.createElement('a');
      pill.classList.add('quick-search-c-pills__item');

      // Use handle if available, otherwise convert value to handle
      const handle = category.data?.handle || name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      pill.href = `/collections/${handle}`;
      pill.textContent = name;

      // Constructor tracking attributes
      pill.setAttribute('data-cnstrc-item', trackingType);
      pill.setAttribute('data-cnstrc-item-section', 'Categories');
      pill.setAttribute('data-cnstrc-item-name', name);
      if (category.data?.id) {
        pill.setAttribute('data-cnstrc-item-id', category.data.id);
      }

      container.appendChild(pill);
    });

    this.#initPillFades(container);
  }

  // ============================================
  // RENDER METHODS - RESULTS STATE
  // ============================================

  /**
   * Render the Results state
   * @param {Array} products - Product results
   * @param {Array} suggestions - Search suggestions
   * @param {Array} categories - Category results
   * @param {Number} productCount - Total product count
   * @param {String} query - Search query
   * @private
   */
  #updateSectionTitle(selector, useFallback) {
    const titleEl = this.wrapper.querySelector(selector);
    if (!titleEl) return;
    const key = useFallback ? 'fallbackTitle' : 'defaultTitle';
    titleEl.textContent = titleEl.dataset[key];
  }

  #renderResultsState(products, suggestions, categories, productCount, query) {
    // Render search suggestions - fallback to popular searches as text links if empty
    if (suggestions.length > 0) {
      this.#renderSuggestions(suggestions, query);
      this.#updateSectionTitle(this.#selectors.suggestionsTitle, false);
    } else {
      // Fallback: render popular searches as text links (no search icons)
      this.#renderPopularSearchTextLinks(
        this.#cachedRecommendations.popularSearches,
        this.wrapper.querySelector(this.#selectors.suggestions),
      );
      this.#updateSectionTitle(this.#selectors.suggestionsTitle, true);
    }

    // Render collection results - fallback to trending collections if empty
    if (categories.length > 0) {
      this.#renderCollectionPills(categories, this.wrapper.querySelector(this.#selectors.collections), 'autosuggest');
      this.#updateSectionTitle(this.#selectors.collectionsTitle, false);
    } else {
      // Fallback: render trending collections
      this.#renderCollectionPills(
        this.#cachedRecommendations.trendingCollections,
        this.wrapper.querySelector(this.#selectors.collections),
        'recommendation',
      );
      this.#updateSectionTitle(this.#selectors.collectionsTitle, true);
    }

    // Render product results - fallback to trending products if empty
    if (products.length > 0) {
      this.#renderProductCards(
        products,
        this.#resultId,
        this.wrapper.querySelector(this.#selectors.products),
        'autosuggest',
      );
      this.#updateProductCount(productCount);
      this.#updateSectionTitle(this.#selectors.productsTitle, false);
    } else {
      this.#renderProductCards(
        this.#cachedRecommendations.trendingProducts,
        this.#cachedRecommendations.resultIds.products,
        this.wrapper.querySelector(this.#selectors.products),
        'recommendation',
      );
      this.#updateProductCount(this.#cachedRecommendations.trendingProducts.length);
      this.#updateSectionTitle(this.#selectors.productsTitle, true);
    }

    // Update View All link
    this.#updateViewAllLink(query);
  }

  /**
   * Render search suggestions
   * @param {Array} suggestions - Suggestion items
   * @param {String} query - Current search query
   * @private
   */
  #renderSuggestions(suggestions, query) {
    const container = this.wrapper.querySelector(this.#selectors.suggestions);
    if (!container) return;
    container.innerHTML = '';

    suggestions.forEach((suggestion) => {
      const suggestionEl = document.createElement('a');
      suggestionEl.classList.add('quick-search-c-suggestions__item');
      suggestionEl.href = `/pages/search?q=${encodeURIComponent(suggestion.value)}`;

      // Constructor tracking attributes
      suggestionEl.setAttribute('data-cnstrc-item', 'autosuggest');
      suggestionEl.setAttribute('data-cnstrc-item-section', 'Search Suggestions');
      suggestionEl.setAttribute('data-cnstrc-item-name', suggestion.value);

      // Highlight matching text
      const highlightedText = this.#highlightQuery(suggestion.value, query);
      suggestionEl.innerHTML = `<span>${highlightedText}</span>`;

      container.appendChild(suggestionEl);
    });
  }

  /**
   * Render product cards using ProductCardBuilder
   * @param {Array} products - Product items
   * @param {String} resultId - Constructor result ID
   * @param {HTMLElement} container - Target container
   * @param {String} trackingType - 'recommendation' or 'autosuggest'
   * @private
   */
  #renderProductCards(products, resultId, container, trackingType = 'autosuggest') {
    if (!container) return;
    container.innerHTML = '';

    if (!products || products.length === 0) return;

    // Set tracking attributes on container
    if (resultId) {
      container.setAttribute('data-cnstrc-result-id', resultId);
    }
    container.setAttribute('data-cnstrc-num-results', products.length);

    // Set other tracking attributes on container
    if (trackingType !== 'autosuggest') {
      container.setAttribute('data-cnstrc-recommendations-pod-id', this.#podIds.trendingProducts);
      container.setAttribute('data-cnstrc-recommendations', '');
    }

    // Determine how many cards to show based on viewport
    const displayCount = window.innerWidth < 990 ? 6 : 6;
    //const thresholdCards = window.innerWidth > 989 ? 3 : 1;

    // Build product cards using ProductCardBuilder
    products.slice(0, displayCount).forEach((product) => {
      let builder;

      if (Array.isArray(product.variations)) {
        builder = new ProductCardBuilder(product.value, product.data?.product_type)
          .makeExtraBaseClass('quick-search-c-results')
          .makeNode(container)
          .makeUrl(product.data?.url)
          .makeMedia(product.data?.media || product.data?.images)
          .makeVariants(product.variations || [])
          .makeVariantId()
          .makePlaceholderTransform()
          .selectColor()
          .makePrices()
          .makeOptions();
        // .makeReviews(product.data?.reviews_count, product.data?.reviews_average)
        // .makeCardImageHidden(idx > thresholdCards)
        // .makeVideo(product.data?.original_video_source);
      } else {
        builder = new PromoTileBuilder()
          .makeExtraBaseClass('quick-search-c-results')
          .makeNode(container)
          .makeImage(product?.data?.image_url, product?.data?.alt || '')
          .makeDestinationUrl(product?.data?.url || '');
      }

      // Set appropriate tracking attributes
      if (trackingType === 'autosuggest') {
        builder.setConstructorAutocompleteAttributes(product, 'Products');
      } else {
        builder.makeConstructorRecommendation(product).setConstructorAttributes(product);
      }

      builder.build();
    });
  }

  /**
   * Update the product count in the header
   * @param {Number} count - Total product count
   * @private
   */
  #updateProductCount(count) {
    const countEl = this.wrapper.querySelector(this.#selectors.productCount);
    if (countEl) {
      countEl.textContent = count.toLocaleString();
    }
  }

  /**
   * Update the View All link with the current query
   * @param {String} query - Search query
   * @private
   */
  #updateViewAllLink(query) {
    const viewAllEl = this.wrapper.querySelector(this.#selectors.viewAll);
    if (viewAllEl) {
      viewAllEl.href = `/pages/search?q=${encodeURIComponent(query)}`;
    }
  }

  // ============================================
  // RENDER METHODS - NO RESULTS STATE
  // ============================================

  /**
   * Render the No Results state
   * @param {String} query - Failed search query
   * @private
   */
  #renderNoResultsState(query) {
    // Set failed query text
    const failedQueryEl = this.wrapper.querySelector(this.#selectors.failedQuery);
    if (failedQueryEl) {
      failedQueryEl.textContent = query;
    }

    // Use cached recommendations for fallback
    const { popularSearches, trendingCollections, trendingProducts, resultIds } = this.#cachedRecommendations;

    // Render fallback popular searches as pills (mobile)
    this.#renderPopularSearchPills(popularSearches, this.wrapper.querySelector(this.#selectors.fallbackPopular));

    // Render fallback popular searches as text links (desktop)
    this.#renderPopularSearchTextLinks(
      popularSearches,
      this.wrapper.querySelector(this.#selectors.fallbackPopularDesktop),
    );

    // Render fallback collections
    this.#renderCollectionPills(
      trendingCollections,
      this.wrapper.querySelector(this.#selectors.fallbackCollections),
      'recommendation',
    );

    // Render fallback trending products
    this.#renderProductCards(
      trendingProducts,
      resultIds.products,
      this.wrapper.querySelector(this.#selectors.fallbackProducts),
      'recommendation',
    );
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  /**
   * Highlight matching query text in suggestion
   * @param {String} text - Full suggestion text
   * @param {String} query - Current search query
   * @returns {String} - HTML string with highlighted text
   * @private
   */
  #highlightQuery(text, query) {
    if (!query) return text;

    // Escape special regex characters in query
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');
    return text.replace(regex, '<strong>$1</strong>');
  }

  /**
   * Cleanup event listeners and timers
   */
  destroy() {
    // Clear timers
    if (this.#debounceTimer) {
      clearTimeout(this.#debounceTimer);
    }

    // Remove event listeners
    if (this.input) {
      this.input.removeEventListener('input', this.#handleInputBound);
      this.input.removeEventListener('keydown', this.#handleKeydownBound);
      this.input.removeEventListener('focus', this.#handleFocusBound);
    }

    if (this.form) {
      this.form.removeEventListener('submit', this.#handleSubmitBound);
    }

    const clearButton = this.wrapper?.querySelector(this.#selectors.clearButton);
    if (clearButton) {
      clearButton.removeEventListener('click', this.#handleClearBound);
    }

    const goButtons = document.querySelectorAll(this.#selectors.goButton);
    goButtons.forEach((btn) => {
      btn.removeEventListener('click', this.#handleGoBound);
    });

    document.removeEventListener('searchDrawerClosed', this.#handleDrawerCloseBound);
    document.removeEventListener('searchDrawerOpened', this.#handleDrawerOpenBound);
    window.removeEventListener('resize', this.#handleResizeBound);
    this.wrapper?.removeEventListener('scroll', this.#handleDrawerScrollBound);
    this.wrapper?.removeEventListener('click', this.#handleAutocompleteClickBound, true);

    this.#cleanupPillFades();
  }
}

export default QuickSearch;
